import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import Loading from '../Components/Loading';

import { API_URL } from '../config/api';

const LeaveBalance = () => {
  const [leaveBalance, setLeaveBalance] = useState(null);
  const [loading, setLoading] = useState(false);
  const [year, setYear] = useState(new Date().getFullYear());

  useEffect(() => {
    fetchLeaveBalance();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year]);

  const fetchLeaveBalance = async () => {
    if (!API_URL) {
      toast.error('API URL is not configured. Please set REACT_APP_API_URL in environment variables.');
      return;
    }

    setLoading(true);
    try {
      const employeeId = localStorage.getItem('id');
      
      if (!employeeId) {
        toast.error('User ID not found. Please login again.');
        return;
      }

      const payload = {
        action: 'get-leave-balance',
        employeeId: employeeId,
        year: year
      };

      const response = await axios.post(API_URL, payload, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      const result = response.data;

      if (result?.success) {
        setLeaveBalance(result.balance);
      } else {
        toast.error(result?.message || 'Failed to fetch leave balance');
      }
    } catch (error) {
      console.error('Error fetching leave balance:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Something went wrong. Please try again.';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="employee-form">
      <section className="content">
        <div className="container-fluid">
          {/* Header with Title */}
          <div className="row mb-3 employee-header-row">
            <div className="col-md-11">
              <h1 className="employee-title">
                Leave Balance
                <i className="fas fa-info-circle ml-2 employee-title-icon"></i>
              </h1>
              <p className="employee-description">
                View your leave balance for the selected year.
              </p>
            </div>
            <div className="col-md-1">
              <div className="form-group leave-balance-form-group">
                <input
                  type="number"
                  className="form-control leave-balance-year-input"
                  placeholder="Year"
                  value={year}
                  onChange={(e) => setYear(parseInt(e.target.value) || new Date().getFullYear())}
                />
              </div>
            </div>
          </div>

          {/* Leave Balance Cards */}
          <div className="row leave-balance-row">
            {loading ? (
              <div className="col-12">
                <Loading size={120} />
              </div>
            ) : leaveBalance ? (
              <>
                <div className="col-md-3">
                  <div className="card employee-content-card leave-balance-card-wrapper">
                    <div className="card-body leave-balance-card-body">
                      <div className="leave-balance-icon leave-balance-icon-casual">
                        <i className="fas fa-calendar-check"></i>
                      </div>
                      <h3 className="leave-balance-title">
                        Casual Leave
                      </h3>
                      <div className="leave-balance-value leave-balance-value-casual">
                        {leaveBalance.casual?.balance || 0}
                      </div>
                      <div className="leave-balance-label">
                        Available
                      </div>
                      <div className="leave-balance-details">
                        <div className="leave-balance-detail-row">
                          <span>Allocated:</span>
                          <span className="leave-balance-detail-value">{leaveBalance.casual?.allocated || 0}</span>
                        </div>
                        <div className="leave-balance-detail-row">
                          <span>Used:</span>
                          <span className="leave-balance-detail-value">{leaveBalance.casual?.used || 0}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="col-md-3">
                  <div className="card employee-content-card leave-balance-card-wrapper">
                    <div className="card-body leave-balance-card-body">
                      <div className="leave-balance-icon leave-balance-icon-sick">
                        <i className="fas fa-heartbeat"></i>
                      </div>
                      <h3 className="leave-balance-title">
                        Sick Leave
                      </h3>
                      <div className="leave-balance-value leave-balance-value-sick">
                        {leaveBalance.sick?.balance || 0}
                      </div>
                      <div className="leave-balance-label">
                        Available
                      </div>
                      <div className="leave-balance-details">
                        <div className="leave-balance-detail-row">
                          <span>Allocated:</span>
                          <span className="leave-balance-detail-value">{leaveBalance.sick?.allocated || 0}</span>
                        </div>
                        <div className="leave-balance-detail-row">
                          <span>Used:</span>
                          <span className="leave-balance-detail-value">{leaveBalance.sick?.used || 0}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="col-md-3">
                  <div className="card employee-content-card leave-balance-card-wrapper">
                    <div className="card-body leave-balance-card-body">
                      <div className="leave-balance-icon leave-balance-icon-earned">
                        <i className="fas fa-star"></i>
                      </div>
                      <h3 className="leave-balance-title">
                        Earned Leave
                      </h3>
                      <div className="leave-balance-value leave-balance-value-earned">
                        {leaveBalance.earned?.balance || 0}
                      </div>
                      <div className="leave-balance-label">
                        Available
                      </div>
                      <div className="leave-balance-details">
                        <div className="leave-balance-detail-row">
                          <span>Allocated:</span>
                          <span className="leave-balance-detail-value">{leaveBalance.earned?.allocated || 0}</span>
                        </div>
                        <div className="leave-balance-detail-row">
                          <span>Used:</span>
                          <span className="leave-balance-detail-value">{leaveBalance.earned?.used || 0}</span>
                        </div>
                        {leaveBalance.earned?.carryForward !== undefined && leaveBalance.earned?.carryForward !== null && (
                          <div className="leave-balance-detail-row">
                            <span>Carry Forward:</span>
                            <span className="leave-balance-detail-value leave-balance-detail-value-earned">{leaveBalance.earned.carryForward}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="col-md-3">
                  <div className="card employee-content-card leave-balance-card-wrapper">
                    <div className="card-body leave-balance-card-body">
                      <div className="leave-balance-icon leave-balance-icon-lop">
                        <i className="fas fa-exclamation-triangle"></i>
                      </div>
                      <h3 className="leave-balance-title">
                        LOP
                      </h3>
                      <div className="leave-balance-value leave-balance-value-lop">
                        {leaveBalance.lop?.used || 0}
                      </div>
                      <div className="leave-balance-label">
                        Days Taken
                      </div>
                      <div className="leave-balance-details">
                        <div className="leave-balance-detail-row">
                          <span>No limit</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <div className="col-12">
                <p className="leave-balance-empty">
                  Unable to load leave balance
                </p>
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default LeaveBalance;

